// What the board shows, worked out from the deals: where each card sits while
// a move is in flight, and what a column adds up to.
//
// Pure, so it can be tested without a router or a drag event, and so the
// arithmetic — the one part of this screen that can be quietly wrong — is in
// a place that has no DOM to hide behind.

import { Currency, convertMinor, splitMinor } from "@crm/shared";
import type { DealListItem, DealState } from "@crm/shared";

/**
 * A state change the board has sent, or tried to. Held per deal beside the
 * server's rows rather than written into them: the rows are the cache's, and
 * a card is drawn in its new column on the strength of THIS record until the
 * server's copy catches up.
 *
 *   saving  the PATCH is in flight; the card is already drawn where it was dropped.
 *   done    the PATCH succeeded; the card stays put until a refetch carries a
 *           version newer than the one the PATCH was sent against, at which
 *           point the server's row says the same thing and this is retired.
 *   stale   a 409 — someone else changed the deal first. The card is drawn
 *           where the SERVER says, and the retry is offered rather than taken.
 *   failed  any other failure, likewise reported on the card in its old column.
 */
export interface PendingMove {
  to: DealState;
  /** The `If-Match` the PATCH carried. */
  version: number;
  status: "saving" | "done" | "stale" | "failed";
  error?: unknown;
}

/** Which column a deal is drawn in, given the move that may be in flight for it. */
export function placedState(deal: DealListItem, move: PendingMove | undefined): DealState {
  if (move === undefined) return deal.state;
  switch (move.status) {
    case "saving":
      return move.to;
    case "done":
      // A newer version from the server outranks the memory of our own write:
      // either it is our write, or it is a later one, and in both cases the
      // server's row is the truth.
      return deal.version > move.version ? deal.state : move.to;
    case "stale":
    case "failed":
      return deal.state;
  }
}

/**
 * A move that has served its purpose: the server's row is newer than the
 * version it was sent against, so the card's column no longer depends on it.
 */
export function moveIsRetired(deal: DealListItem | undefined, move: PendingMove): boolean {
  return move.status === "done" && (deal === undefined || deal.version > move.version);
}

/**
 * Which figure a column adds up. Each is a number the list row already
 * carries (or one arithmetic step from it), so changing the measure re-sums on
 * the client and asks the API nothing.
 *
 *   contracted  what the deal's terms are worth, when every clause has a value
 *   weighted    the same, discounted by the deal's own win probability — the
 *               pipeline's valuation of an open deal
 *   billed      what has actually gone out on issued documents
 */
export const BOARD_MEASURES = ["contracted", "weighted", "billed"] as const;
export type BoardMeasure = (typeof BOARD_MEASURES)[number];

export function boardMeasureLabel(measure: BoardMeasure): string {
  switch (measure) {
    case "contracted":
      return "Our share: contracted, or estimated";
    case "weighted":
      return "Our share, weighted by win %";
    case "billed":
      return "Our share billed to date";
  }
}

/** Whether a figure is money the terms or the documents state, or an ESTIMATE. */
export type FigureKind = "real" | "estimate";

/** One deal's figure under a measure, and which kind of money it is. */
export interface DealFigure {
  minor: number;
  kind: FigureKind;
}

/** The stages before a deal is won: nothing is contracted yet, so an estimate is all there is. */
const BEFORE_WON: ReadonlySet<DealState> = new Set<DealState>([
  "lead",
  "qualified",
  "demoed",
  "proposal",
  "decision_maker_bought_in",
]);

/**
 * OUR share of a deal's ESTIMATED first year: the implementation estimate plus
 * twelve months of the recurring estimate, each at the share the API resolved
 * for it (the deal's override, its agreement, or the assumed share — named in
 * `estimated_share_source`). Null when nobody has estimated the deal.
 */
export function estimatedFirstYearMinor(deal: DealListItem): number | null {
  return deal.estimated_first_year_our_share_minor;
}

/**
 * One deal's figure under a measure, or null when there is none to show.
 *
 * Every figure is OUR share, after any partner's: the board shows what we earn,
 * not what the customer pays. Billed is always real: what went out is a fact. Otherwise a deal before won
 * shows its ESTIMATE, since nothing is contracted yet; a won or later deal shows
 * what its terms are worth, falling back to its estimate only while its terms
 * state no value (a deal won before anybody entered its contract). A lost or
 * ended deal is never estimated. The kind travels with the figure, so no screen can show an
 * estimate as if it were contracted.
 *
 * The weighting uses `splitMinor` — the same round-once-at-the-end the SQL
 * side uses for a share of a gross — so it agrees to the cent with the
 * pipeline report's treatment of the same deal.
 */
export function figureOf(deal: DealListItem, measure: BoardMeasure): DealFigure | null {
  if (measure === "billed") return { minor: deal.billed_to_date_our_share_minor, kind: "real" };
  const contracted = deal.contracted_our_share_minor;
  const estimate =
    deal.state === "lost" || deal.state === "ended" ? null : estimatedFirstYearMinor(deal);
  const useEstimate =
    estimate !== null && (BEFORE_WON.has(deal.state) || contracted === null || contracted === 0);
  const base = useEstimate ? estimate : contracted;
  if (base === null) return null;
  const minor = measure === "weighted" ? splitMinor(base, deal.win_prob) : base;
  return { minor, kind: useEstimate ? "estimate" : "real" };
}

/** One currency's slice of a column. Never merged with another's. */
export interface ColumnTotal {
  currency: Currency;
  /** The measure summed over the deals whose figure is REAL money. */
  minor: number;
  /** The measure summed over the deals whose figure is an ESTIMATE. Never added to `minor`. */
  estimateMinor: number;
  /** How many deals `estimateMinor` is the sum of. */
  estimateCount: number;
  /** Every deal in this currency, with a figure or without. */
  count: number;
  /**
   * How many of `count` have no figure under this measure. They are not in
   * `minor`, and a total that skipped them silently would read as whole.
   */
  unknownCount: number;
}

/**
 * What a column adds up to, one entry per currency in contract order and none
 * for a currency with no deal in the column. CAD and USD are never added raw:
 * one figure needs a rate, which `convertColumnTotals` applies, and with no
 * rate a column holding one of each shows two figures. Real money and
 * estimates are kept apart the same way.
 */
export function columnTotals(
  deals: readonly DealListItem[],
  measure: BoardMeasure,
): readonly ColumnTotal[] {
  const buckets = new Map<Currency, ColumnTotal>();
  for (const deal of deals) {
    const bucket = buckets.get(deal.currency) ?? {
      currency: deal.currency,
      minor: 0,
      estimateMinor: 0,
      estimateCount: 0,
      count: 0,
      unknownCount: 0,
    };
    const figure = figureOf(deal, measure);
    bucket.count += 1;
    if (figure === null) bucket.unknownCount += 1;
    else if (figure.kind === "estimate") {
      bucket.estimateMinor += figure.minor;
      bucket.estimateCount += 1;
    } else bucket.minor += figure.minor;
    buckets.set(deal.currency, bucket);
  }
  return Currency.options
    .map((currency) => buckets.get(currency))
    .filter((total): total is ColumnTotal => total !== undefined);
}

/** A column's figure in ONE currency, and whether any of it had to be converted to get there. */
export interface ConvertedColumnTotal extends ColumnTotal {
  /** True when a deal in another currency was folded in, so the figure is approximate. */
  converted: boolean;
}

/**
 * The column's per-currency totals as one figure in the reader's report
 * currency, for the column header. The cards keep each deal's own currency;
 * only the header, which adds deals up, is converted.
 *
 * Each currency's total converts ONCE (the server's display rule, so a column
 * and a report about the same deals round the same way), at `usdToCad`, the
 * rate in force today: a pipeline is about money not yet billed, so there is no
 * past date to pin it to. Real money and estimates stay apart, as above.
 *
 * Null when a total is in another currency and there is no rate to convert it
 * with. The caller then shows the per-currency lines rather than a figure that
 * left some deals out or counted USD as CAD.
 */
export function convertColumnTotals(
  totals: readonly ColumnTotal[],
  target: Currency,
  usdToCad: number | null,
): ConvertedColumnTotal | null {
  const converted = totals.some((total) => total.currency !== target);
  if (converted && usdToCad === null) return null;
  const into = (minor: number, from: Currency): number =>
    usdToCad === null ? minor : convertMinor(minor, from, target, usdToCad);
  return totals.reduce<ConvertedColumnTotal>(
    (sum, total) => ({
      currency: target,
      minor: sum.minor + into(total.minor, total.currency),
      estimateMinor: sum.estimateMinor + into(total.estimateMinor, total.currency),
      estimateCount: sum.estimateCount + total.estimateCount,
      count: sum.count + total.count,
      unknownCount: sum.unknownCount + total.unknownCount,
      converted,
    }),
    {
      currency: target,
      minor: 0,
      estimateMinor: 0,
      estimateCount: 0,
      count: 0,
      unknownCount: 0,
      converted,
    },
  );
}
